/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EndpointsManager - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file EndpointsManager.tsx
 * @version 1.0.0
 * @description Gestión CRUD de endpoints de IA
 * @author WASP Development Team
 * 
 * FUNCIONALIDADES:
 * - Listar endpoints con filtros
 * - Crear nuevo endpoint
 * - Editar endpoint existente
 * - Eliminar endpoint
 * - Toggle enable/disable
 * - Filtrar por capacidad
 * - Búsqueda por nombre
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Alert,
  Dropdown,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faToggleOn,
  faToggleOff,
  faSearch,
  faFilter,
  faEllipsisV,
  faCheckCircle,
  faTimesCircle,
  faBolt,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAIEndpoints } from '../../../../../hooks/useAIEndpoints';
import {
  AIEndpoint,
  AIEndpointFormData,
  AICapability,
  AI_CAPABILITIES,
  AI_CAPABILITY_LABELS
} from '../../../../../types/ai.types';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN GOD MODE
// ═══════════════════════════════════════════════════════════════════════════

// TODO: Cambiar esto por detección de endpoint cuando esté listo
const IS_GOD_MODE = false; // Hardcoded por ahora

// ═══════════════════════════════════════════════════════════════════════════
// ENDPOINTS FIJOS (siempre presentes)
// ═══════════════════════════════════════════════════════════════════════════

interface FixedEndpointMetadata {
  id: string;
  name: string;
  type: string;
  url: string;
  priority: number;
  core: boolean;
  demo: boolean;
  removable: boolean;
  capabilities: string[];
  primary: string;
}

const FIXED_ENDPOINTS: FixedEndpointMetadata[] = [
  {
    id: 'lhp-irc',
    name: 'LinkHub Network (LHP)',
    type: 'linkhub-net',
    url: 'hub.linuxcorp.net:6697',
    priority: 1,
    core: false,
    demo: false,
    removable: true, // Puede eliminarse en God Mode
    capabilities: ['text'],
    primary: 'text'
  },
  {
    id: 'anubis-core',
    name: 'Anubis AI',
    type: 'llm-custom',
    url: 'backend.anubisai.net/api/anubis',
    priority: 2,
    core: true, // CORE: No eliminable nunca
    demo: false,
    removable: false,
    capabilities: ['text'],
    primary: 'text'
  },
  {
    id: 'openrouter',
    name: 'Friday Fallback',
    type: 'llm-ml',
    url: 'fallback.anubisai.net/api/fday',
    priority: 3,
    core: false,
    demo: false,
    removable: true, // Puede eliminarse en God Mode
    capabilities: ['text', 'code'],
    primary: 'text'
  },
  {
    id: 'ml-core',
    name: 'ML Model',
    type: 'ml-custom',
    url: 'backend.anubisai.net/api/ml',
    priority: 4,
    core: true, // CORE: No eliminable nunca
    demo: false,
    removable: false,
    capabilities: ['text'],
    primary: 'text'
  },
  {
    id: 'demo-groq',
    name: 'Demo LLM Gratuito (Groq)',
    type: 'openai-compatible',
    url: 'api.groq.com/openai/v1/chat',
    priority: 5,
    core: false,
    demo: true,
    removable: false, // DEMO: No eliminable nunca
    capabilities: ['text'],
    primary: 'text'
  }
];

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const EndpointsManager: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────────
  // Hooks
  // ───────────────────────────────────────────────────────────────────────
  
  const {
    endpoints,
    isLoading,
    error,
    createEndpoint,
    updateEndpoint,
    deleteEndpoint,
    toggleEndpoint,
    filterEndpoints,
    refreshEndpoints
  } = useAIEndpoints();

  // ───────────────────────────────────────────────────────────────────────
  // Estado Local
  // ───────────────────────────────────────────────────────────────────────
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCapability, setFilterCapability] = useState<AICapability | ''>('');
  const [filterEnabled, setFilterEnabled] = useState<boolean | ''>('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Endpoint seleccionado
  const [selectedEndpoint, setSelectedEndpoint] = useState<AIEndpoint | null>(null);
  
  // Estado local para endpoints fijos (para toggle sin backend)
  const [fixedEndpointsState, setFixedEndpointsState] = useState<{ [id: string]: { enabled: boolean } }>({
    'lhp-irc': { enabled: true },
    'anubis-core': { enabled: true },
    'openrouter': { enabled: true },
    'ml-core': { enabled: true },
    'demo-groq': { enabled: false }
  });
  
  // Form state
  const [formData, setFormData] = useState<AIEndpointFormData>({
    name: '',
    type: '',
    capabilities: [],
    primary: 'text',
    url: '',
    enabled: true,
    priority: 1,
    metadata: {
      description: '',
      model: '',
      maxTokens: 1000,
      temperature: 0.7,
      topP: 0.9,
      frequencyPenalty: 0.0,
      presencePenalty: 0.0
    }
  });

  // ───────────────────────────────────────────────────────────────────────
  // Helpers para God Mode
  // ───────────────────────────────────────────────────────────────────────

  const isFixedEndpoint = (endpointId: string): boolean => {
    return FIXED_ENDPOINTS.some(fe => fe.id === endpointId);
  };

  const isCoreEndpoint = (endpointId: string): boolean => {
    const fixed = FIXED_ENDPOINTS.find(fe => fe.id === endpointId);
    return fixed?.core || false;
  };

  const isDemoEndpoint = (endpointId: string): boolean => {
    const fixed = FIXED_ENDPOINTS.find(fe => fe.id === endpointId);
    return fixed?.demo || false;
  };

  const isRemovableEndpoint = (endpointId: string): boolean => {
    const fixed = FIXED_ENDPOINTS.find(fe => fe.id === endpointId);
    if (!fixed) return true; // Endpoints custom siempre removibles
    return IS_GOD_MODE ? fixed.removable : false;
  };

  const isEditableEndpoint = (endpointId: string): boolean => {
    if (!isFixedEndpoint(endpointId)) return true; // Custom endpoints siempre editables
    if (IS_GOD_MODE) return true; // God Mode: todo editable
    return !isCoreEndpoint(endpointId); // Sin God Mode: CORE no editable
  };

  // ───────────────────────────────────────────────────────────────────────
  // Combinar endpoints fijos con endpoints del backend
  // ───────────────────────────────────────────────────────────────────────

  const allEndpoints = useMemo(() => {
    // Convertir fixed endpoints a formato AIEndpoint con estado local
    const fixedAsAIEndpoints: AIEndpoint[] = FIXED_ENDPOINTS.map(fixed => ({
      id: fixed.id,
      name: fixed.name,
      type: fixed.type,
      capabilities: fixed.capabilities,
      primary: fixed.primary,
      url: fixed.url,
      enabled: fixedEndpointsState[fixed.id]?.enabled ?? true,
      priority: fixed.priority,
      metadata: {
        isCore: fixed.core,
        isDemo: fixed.demo,
        isRemovable: fixed.removable
      }
    } as AIEndpoint));

    // Combinar con endpoints del backend
    return [...fixedAsAIEndpoints, ...endpoints];
  }, [endpoints, fixedEndpointsState]);

  // ───────────────────────────────────────────────────────────────────────
  // Filtrado de endpoints
  // ───────────────────────────────────────────────────────────────────────
  
  const filteredEndpoints = useMemo(() => {
    let filtered = allEndpoints;
    
    // Aplicar filtros
    if (filterCapability || filterEnabled !== '' || searchTerm) {
      filtered = allEndpoints.filter(ep => {
        let matches = true;
        
        if (filterCapability && !ep.capabilities.includes(filterCapability)) {
          matches = false;
        }
        
        if (filterEnabled !== '' && ep.enabled !== filterEnabled) {
          matches = false;
        }
        
        if (searchTerm && !ep.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          matches = false;
        }
        
        return matches;
      });
    }
    
    return filtered.sort((a, b) => a.priority - b.priority);
  }, [allEndpoints, filterCapability, filterEnabled, searchTerm]);

  // ───────────────────────────────────────────────────────────────────────
  // Handlers - Modals
  // ───────────────────────────────────────────────────────────────────────
  
  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      type: '',
      capabilities: [],
      primary: 'text',
      url: '',
      enabled: true,
      priority: 1,
      metadata: {
        description: '',
        model: '',
        maxTokens: 1000,
        temperature: 0.7,
        topP: 0.9,
        frequencyPenalty: 0.0,
        presencePenalty: 0.0
      }
    });
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (endpoint: AIEndpoint) => {
    // Verificar si es editable
    if (!isEditableEndpoint(endpoint.id)) {
      alert('Este endpoint CORE no es editable sin God Mode');
      return;
    }
    
    setSelectedEndpoint(endpoint);
    setFormData({
      name: endpoint.name,
      type: endpoint.type,
      capabilities: endpoint.capabilities,
      primary: endpoint.primary as AICapability,
      url: endpoint.url,
      enabled: endpoint.enabled,
      priority: endpoint.priority,
      metadata: {
        description: endpoint.metadata?.description || '',
        model: endpoint.metadata?.model || '',
        maxTokens: endpoint.metadata?.maxTokens || 1000,
        temperature: endpoint.metadata?.temperature || 0.7,
        topP: endpoint.metadata?.topP || 0.9,
        frequencyPenalty: endpoint.metadata?.frequencyPenalty || 0.0,
        presencePenalty: endpoint.metadata?.presencePenalty || 0.0
      }
    });
    setShowEditModal(true);
  };

  const handleOpenDeleteModal = (endpoint: AIEndpoint) => {
    // Verificar si es removible
    if (!isRemovableEndpoint(endpoint.id)) {
      if (isCoreEndpoint(endpoint.id)) {
        alert('Los endpoints CORE no pueden eliminarse');
      } else if (isDemoEndpoint(endpoint.id)) {
        alert('Los endpoints DEMO no pueden eliminarse');
      } else {
        alert('Este endpoint no puede eliminarse');
      }
      return;
    }
    
    setSelectedEndpoint(endpoint);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedEndpoint(null);
  };

  // ───────────────────────────────────────────────────────────────────────
  // Handlers - CRUD
  // ───────────────────────────────────────────────────────────────────────
  
  const handleCreate = async () => {
    const result = await createEndpoint(formData);
    if (result) {
      handleCloseModals();
    }
  };

  const handleUpdate = async () => {
    if (!selectedEndpoint) return;
    const result = await updateEndpoint(selectedEndpoint.id, formData);
    if (result) {
      handleCloseModals();
    }
  };

  const handleDelete = async () => {
    if (!selectedEndpoint) return;
    const result = await deleteEndpoint(selectedEndpoint.id);
    if (result) {
      handleCloseModals();
    }
  };

  const handleToggle = async (endpoint: AIEndpoint) => {
    // Si es endpoint fijo, solo cambiar estado local
    if (isFixedEndpoint(endpoint.id)) {
      setFixedEndpointsState(prev => ({
        ...prev,
        [endpoint.id]: { enabled: !endpoint.enabled }
      }));
      return;
    }
    
    // Si es endpoint custom, llamar al backend
    await toggleEndpoint(endpoint.id, !endpoint.enabled);
  };

  // ───────────────────────────────────────────────────────────────────────
  // Handlers - Form
  // ───────────────────────────────────────────────────────────────────────
  
  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMetadataChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value
      }
    }));
  };

  const handleCapabilityToggle = (capability: AICapability) => {
    setFormData(prev => {
      const capabilities = prev.capabilities.includes(capability)
        ? prev.capabilities.filter(c => c !== capability)
        : [...prev.capabilities, capability];
      
      return {
        ...prev,
        capabilities
      };
    });
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Tabla de endpoints
  // ───────────────────────────────────────────────────────────────────────
  
  const renderEndpointsTable = () => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando endpoints...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>Error:</strong> {error}
        </Alert>
      );
    }

    if (filteredEndpoints.length === 0) {
      return (
        <Alert variant="info">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {endpoints.length === 0 
            ? 'No hay endpoints configurados. Crea uno para comenzar.'
            : 'No se encontraron endpoints con los filtros aplicados.'
          }
        </Alert>
      );
    }

    return (
      <Table responsive hover className="mb-0" size="sm" style={{ maxWidth: '100%' }}>
        <thead className="bg-light">
          <tr>
            <th style={{ padding: '10px 12px' }}>Estado</th>
            <th style={{ padding: '10px 12px' }}>Nombre</th>
            <th style={{ padding: '10px 12px' }}>Tipo</th>
            <th style={{ padding: '10px 12px' }}>Capacidades</th>
            <th style={{ padding: '10px 12px' }}>URL</th>
            <th style={{ padding: '10px 12px' }}>#</th>
            <th style={{ padding: '10px 12px' }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredEndpoints.map(endpoint => (
            <tr key={endpoint.id} style={{ height: '45px' }}>
              <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                <Button
                  variant={endpoint.enabled ? 'success' : 'secondary'}
                  size="sm"
                  onClick={() => handleToggle(endpoint)}
                  className="d-flex align-items-center"
                  style={{ minWidth: 70, padding: '4px 8px', fontSize: '0.8rem' }}
                >
                  <FontAwesomeIcon 
                    icon={endpoint.enabled ? faCheckCircle : faTimesCircle} 
                    className="me-1" 
                    style={{ fontSize: '0.75rem' }}
                  />
                  {endpoint.enabled ? 'Activo' : 'Inactivo'}
                </Button>
              </td>
              <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" style={{ fontSize: '0.9rem' }} />
                  <strong style={{ fontSize: '0.9rem' }}>{endpoint.name}</strong>
                  
                  {/* Badge CORE */}
                  {isCoreEndpoint(endpoint.id) && (
                    <Badge bg="warning" className="ms-2" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                      CORE
                    </Badge>
                  )}
                  
                  {/* Badge DEMO */}
                  {isDemoEndpoint(endpoint.id) && (
                    <Badge bg="info" className="ms-2" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                      DEMO
                    </Badge>
                  )}
                  
                  {/* Indicador God Mode */}
                  {IS_GOD_MODE && isFixedEndpoint(endpoint.id) && (
                    <Badge bg="danger" className="ms-2" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                      GOD MODE
                    </Badge>
                  )}
                </div>
                {endpoint.metadata?.description && (
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                    {endpoint.metadata.description}
                  </small>
                )}
              </td>
              <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                <Badge bg="secondary" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                  {endpoint.type}
                </Badge>
                {endpoint.metadata?.model && (
                  <small className="d-block text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                    {endpoint.metadata.model}
                  </small>
                )}
              </td>
              <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                <div className="d-flex flex-wrap gap-1">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Capacidad primaria: {AI_CAPABILITY_LABELS[endpoint.primary as AICapability]}
                      </Tooltip>
                    }
                  >
                    <Badge bg="primary" className="me-1" style={{ fontSize: '0.7rem', padding: '3px 6px' }}>
                      {endpoint.primary}
                    </Badge>
                  </OverlayTrigger>
                  {endpoint.capabilities
                    .filter(c => c !== endpoint.primary)
                    .map(cap => (
                      <Badge key={cap} bg="info" className="me-1" style={{ fontSize: '0.7rem', padding: '3px 6px' }}>
                        {cap}
                      </Badge>
                    ))
                  }
                </div>
              </td>
              <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                <small className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>
                  {endpoint.url.length > 35 
                    ? endpoint.url.substring(0, 25) + '...' 
                    : endpoint.url
                  }
                </small>
              </td>
              <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                <Badge bg={endpoint.priority <= 3 ? 'success' : 'warning'} style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                  {endpoint.priority}
                </Badge>
              </td>
              <td className="text-end" style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                <Dropdown>
                  <Dropdown.Toggle 
                    variant="light" 
                    size="sm" 
                    id={`dropdown-${endpoint.id}`}
                    style={{ padding: '4px 8px' }}
                  >
                    <FontAwesomeIcon icon={faEllipsisV} style={{ fontSize: '0.9rem' }} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item 
                      onClick={() => handleOpenEditModal(endpoint)}
                      disabled={!isEditableEndpoint(endpoint.id)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      Editar
                      {!isEditableEndpoint(endpoint.id) && (
                        <small className="text-muted ms-2">(CORE bloqueado)</small>
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleToggle(endpoint)}>
                      <FontAwesomeIcon 
                        icon={endpoint.enabled ? faToggleOff : faToggleOn} 
                        className="me-2" 
                      />
                      {endpoint.enabled ? 'Desactivar' : 'Activar'}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item 
                      onClick={() => handleOpenDeleteModal(endpoint)}
                      className="text-danger"
                      disabled={!isRemovableEndpoint(endpoint.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Eliminar
                      {!isRemovableEndpoint(endpoint.id) && (
                        <small className="text-muted ms-2">(No eliminable)</small>
                      )}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Modal de formulario
  // ───────────────────────────────────────────────────────────────────────
  
  const renderFormModal = () => {
    const isEdit = showEditModal;
    const title = isEdit ? 'Editar Endpoint' : 'Crear Nuevo Endpoint';
    const show = showCreateModal || showEditModal;

    return (
      <Modal show={show} onHide={handleCloseModals} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Ej: IRC GPT-4"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                    placeholder="Ej: openai, anthropic, custom"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>URL del Endpoint *</Form.Label>
              <Form.Control
                type="url"
                value={formData.url}
                onChange={(e) => handleFormChange('url', e.target.value)}
                placeholder="https://api.example.com/v1/completions"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.metadata?.description || ''}
                onChange={(e) => handleMetadataChange('description', e.target.value)}
                placeholder="Descripción breve del endpoint"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Modelo</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.metadata?.model || ''}
                    onChange={(e) => handleMetadataChange('model', e.target.value)}
                    placeholder="Ej: gpt-4, claude-3"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Prioridad</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={formData.priority}
                    onChange={(e) => handleFormChange('priority', parseInt(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Menor = más prioritario
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Check
                    type="switch"
                    id="enabled-switch"
                    label={formData.enabled ? 'Activo' : 'Inactivo'}
                    checked={formData.enabled}
                    onChange={(e) => handleFormChange('enabled', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Capacidades *</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {AI_CAPABILITIES.map(cap => (
                  <Form.Check
                    key={cap}
                    type="checkbox"
                    id={`cap-${cap}`}
                    label={AI_CAPABILITY_LABELS[cap]}
                    checked={formData.capabilities.includes(cap)}
                    onChange={() => handleCapabilityToggle(cap)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Capacidad Primaria *</Form.Label>
              <Form.Select
                value={formData.primary}
                onChange={(e) => handleFormChange('primary', e.target.value)}
              >
                {formData.capabilities.map(cap => (
                  <option key={cap} value={cap}>
                    {AI_CAPABILITY_LABELS[cap]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModals}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={isEdit ? handleUpdate : handleCreate}
            disabled={!formData.name || !formData.url || formData.capabilities.length === 0}
          >
            {isEdit ? 'Actualizar' : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Modal de confirmación de eliminación
  // ───────────────────────────────────────────────────────────────────────
  
  const renderDeleteModal = () => (
    <Modal show={showDeleteModal} onHide={handleCloseModals}>
      <Modal.Header closeButton>
        <Modal.Title>Confirmar Eliminación</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          ¿Estás seguro de que deseas eliminar el endpoint <strong>{selectedEndpoint?.name}</strong>?
        </Alert>
        <p className="mb-0">Esta acción no se puede deshacer.</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseModals}>
          Cancelar
        </Button>
        <Button variant="danger" onClick={handleDelete}>
          Eliminar
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // ───────────────────────────────────────────────────────────────────────
  // Render Principal
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div>
      {/* Indicador God Mode */}
      {IS_GOD_MODE && (
        <Alert variant="danger" className="mb-3" style={{ padding: '12px 16px' }}>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <strong style={{ fontSize: '0.95rem' }}>⚠️ GOD MODE ACTIVO</strong>
              <br />
              <small style={{ fontSize: '0.8rem' }}>Todos los endpoints son editables. CORE y DEMO siguen protegidos contra eliminación.</small>
            </div>
            <Badge bg="danger" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
              GOD MODE
            </Badge>
          </div>
        </Alert>
      )}

      {/* Toolbar */}
      <Row className="mb-3" style={{ border: '0px solid blue', maxWidth: '99%' }}>
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por nombre, tipo o URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select
            value={filterCapability}
            onChange={(e) => setFilterCapability(e.target.value as AICapability | '')}
          >
            <option value="">Todas las capacidades</option>
            {AI_CAPABILITIES.map(cap => (
              <option key={cap} value={cap}>
                {AI_CAPABILITY_LABELS[cap]}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3} className="text-end">
          <Button 
            variant="primary" 
            onClick={handleOpenCreateModal}
            className="w-100"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Nuevo Endpoint
          </Button>
        </Col>
      </Row>

      {/* Tabla */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {renderEndpointsTable()}
        </Card.Body>
      </Card>

      {/* Modals */}
      {renderFormModal()}
      {renderDeleteModal()}
    </div>
  );
};

export default EndpointsManager;

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════